"use client";

import { useCallback, useEffect, useRef } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { processSteps } from "@/content/process";

export function ProcessSection() {
  const progressRef = useRef<HTMLDivElement>(null);
  const progressTimeoutRef = useRef<number>(null);

  const handleReveal = useCallback(() => {
    if (progressTimeoutRef.current) {
      window.clearTimeout(progressTimeoutRef.current);
    }

    progressTimeoutRef.current = window.setTimeout(() => {
      if (progressRef.current) {
        progressRef.current.style.width = "100%";
      }
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (progressTimeoutRef.current) {
        window.clearTimeout(progressTimeoutRef.current);
      }
    };
  }, []);

  const sectionRef = useRevealOnScroll<HTMLElement>({
    threshold: 0.3,
    staggerInterval: 100,
    onReveal: handleReveal,
  });

  const steps = processSteps;

  return (
    <section
      ref={sectionRef}
      id="process"
      className="bg-luxury-cream relative overflow-hidden py-24 lg:py-32"
    >
      {/* Background Decoration */}
      <div className="bg-luxury-green/5 absolute top-0 right-0 h-96 w-96 rounded-full blur-3xl" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-12">
        {/* Section Header */}
        <div className="reveal mb-20 text-center">
          <div className="text-luxury-green mb-6 inline-flex items-center space-x-3">
            <div className="bg-luxury-green h-0.5 w-12" />
            <span className="text-sm font-semibold tracking-[0.3em] uppercase">
              Процесс работы
            </span>
            <div className="bg-luxury-green h-0.5 w-12" />
          </div>
          <h2 className="font-display text-luxury-dark mb-6 text-5xl font-bold lg:text-7xl">
            Как мы работаем
          </h2>
          <p className="text-luxury-stone mx-auto max-w-3xl text-xl">
            Прозрачный и понятный процесс от первого звонка до сдачи объекта.
            Каждый этап контролируется нашими специалистами.
          </p>
        </div>

        {/* Desktop Process Timeline */}
        <div className="hidden lg:block">
          <div className="relative">
            <div
              className="bg-luxury-green/20 absolute top-10 right-0 left-0 h-0.5"
              style={{ zIndex: -1 }}
            />
            <div
              ref={progressRef}
              className="from-luxury-green to-luxury-green-light absolute top-10 left-0 h-0.5 bg-gradient-to-r transition-all duration-[1500ms] ease-out"
              style={{ width: "0%", zIndex: 0 }}
            />

            <div className="relative z-10 grid grid-cols-5 gap-8">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className="process-step reveal"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex flex-col items-center">
                    <div className="step-indicator">
                      <div className="step-circle">
                        <span className="step-number">{step.number}</span>
                      </div>
                    </div>
                    <div className="step-content">
                      <div className="step-icon-wrapper">
                        <step.icon className="text-luxury-green h-8 w-8" />
                      </div>
                      <h3 className="step-title">{step.title}</h3>
                      <p className="step-description">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Process Timeline */}
        <div className="space-y-8 lg:hidden">
          {steps.map((step, index) => (
            <div
              key={index}
              className="process-step-mobile reveal"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="step-circle-mobile">
                    <span className="step-number-mobile">{step.number}</span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="bg-luxury-green/20 mt-4 w-0.5 flex-1" />
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <div className="mb-4">
                    <step.icon className="text-luxury-green h-8 w-8" />
                  </div>
                  <h3 className="font-display text-luxury-dark mb-3 text-2xl font-bold">
                    {step.title}
                  </h3>
                  <p className="text-luxury-stone leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .step-indicator {
          position: relative;
          height: 5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 2rem;
        }

        .step-circle {
          position: relative;
          width: 5rem;
          height: 5rem;
          background: white;
          border: 3px solid #22c55e;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 10px 30px rgba(34, 197, 94, 0.15);
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          z-index: 2;
        }

        .process-step:hover .step-circle {
          transform: scale(1.1);
          border-width: 4px;
          box-shadow: 0 15px 40px rgba(34, 197, 94, 0.25);
        }

        .step-number {
          font-family: var(--font-display);
          font-size: 1.5rem;
          font-weight: 700;
          color: #22c55e;
          line-height: 1;
        }

        .step-content {
          text-align: center;
        }

        .step-icon-wrapper {
          width: 4rem;
          height: 4rem;
          margin: 0 auto 1.5rem;
          background: linear-gradient(
            135deg,
            rgba(34, 197, 94, 0.1),
            rgba(134, 239, 172, 0.1)
          );
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.4s ease;
        }

        .process-step:hover .step-icon-wrapper {
          background: linear-gradient(
            135deg,
            rgba(34, 197, 94, 0.2),
            rgba(134, 239, 172, 0.2)
          );
          transform: translateY(-4px);
        }

        .step-title {
          font-family: var(--font-display);
          font-size: 1.5rem;
          font-weight: 700;
          color: #0a0a0a;
          margin-bottom: 0.75rem;
          line-height: 1.2;
        }

        .step-description {
          color: #8b8680;
          line-height: 1.6;
          font-size: 0.9375rem;
        }

        .step-circle-mobile {
          width: 4rem;
          height: 4rem;
          background: white;
          border: 3px solid #22c55e;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 20px rgba(34, 197, 94, 0.15);
          flex-shrink: 0;
        }

        .step-number-mobile {
          font-family: var(--font-display);
          font-size: 1.25rem;
          font-weight: 700;
          color: #22c55e;
          line-height: 1;
        }
      `}</style>
    </section>
  );
}
